import { useState } from 'react';
import axios from 'axios';
import { queryCache } from 'react-query';

/**
 * Options for customizing the unenroll hook behavior
 */
interface UnenrollOptions {
  /** Callback to run after successful unenrollment */
  onSuccess?: () => void;
  /** Callback to run if unenrollment fails */
  onError?: (error: any) => void;
  /** Path to redirect to after successful unenrollment */
  redirectPath?: string;
}

/**
 * State for the unenroll hook
 */
interface UnenrollState {
  /** Whether an unenroll operation is currently in progress */
  isLoading: boolean;
  /** Error from the most recent unenroll attempt, if any */
  error: Error | null;
  /** Whether the confirmation dialog is open */
  isConfirmationOpen: boolean;
}

/**
 * Custom hook for handling course unenrollment
 *
 * Provides a standardized way to handle unenrolling from courses including:
 * - Dialog management (open/close)
 * - API call to unenroll
 * - Error handling with specific messages
 * - Cache invalidation to update UI
 */
export function useUnenroll(options: UnenrollOptions = {}) {
  // State for tracking loading, errors, and dialog state
  const [state, setState] = useState<UnenrollState>({
    isLoading: false,
    error: null,
    isConfirmationOpen: false,
  });

  // Track the course being unenrolled from
  const [courseToUnenroll, setCourseToUnenroll] = useState<{ id: string; userId: string } | null>(null);

  // Use queryCache for cache invalidation in react-query v2

  /**
   * Opens the confirmation dialog with course and user information
   */
  const openConfirmation = (courseId: string, userId: string) => {
    setCourseToUnenroll({ id: courseId, userId });
    setState((prev) => ({ ...prev, isConfirmationOpen: true }));
  };

  /**
   * Closes the confirmation dialog
   */
  const closeConfirmation = () => {
    setState((prev) => ({ ...prev, isConfirmationOpen: false }));
  };

  /**
   * Executes the unenrollment API call
   */
  const executeUnenroll = async () => {
    if (!courseToUnenroll) return;

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // Perform the API call
      await axios.delete(`/api/v3/courses/${courseToUnenroll.id}/users/${courseToUnenroll.userId}`);

      // Invalidate related queries to trigger refetching (using queryCache for v2)
      queryCache.invalidateQueries(['libraryCourses']);
      queryCache.invalidateQueries(['userCourses']);
      queryCache.invalidateQueries(['course', courseToUnenroll.id]);
      queryCache.invalidateQueries(['myCourses']);

      // Call success callback if provided
      if (options.onSuccess) {
        options.onSuccess();
      }

      // Handle redirection if path provided
      if (options.redirectPath) {
        window.location.href = options.redirectPath;
      }

      // Update state
      setState((prev) => ({
        ...prev,
        isLoading: false,
        isConfirmationOpen: false,
      }));
    } catch (error) {
      console.error('Error unenrolling from course:', error);

      // Set error state
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error as Error,
      }));

      // Call error callback if provided
      if (options.onError) {
        options.onError(error);
      }
    }
  };

  /**
   * Gets an appropriate error message based on the error type and status code
   */
  const getErrorMessage = () => {
    if (!state.error) return '';

    // Check for Axios errors with status codes
    // Using type guard instead of axios.isAxiosError which isn't available in this axios version
    if (state.error && 'response' in state.error) {
      const status = (state.error.response as any).status;

      if (status === 403) {
        return 'You do not have permission to unenroll from this course.';
      } else if (status === 404) {
        return 'This course enrollment was not found.';
      } else if (status === 500) {
        return 'Server error occurred. Please try again later.';
      } else if (status === 429) {
        return 'Too many requests. Please wait a moment and try again.';
      } else if (status === 401) {
        return 'Authentication error. Please sign in again.';
      }
    }

    // Network errors
    if (state.error.message === 'Network Error') {
      return 'Network error. Please check your internet connection and try again.';
    }

    // Default error message
    return 'Failed to unenroll from course. Please try again.';
  };

  // Return all the necessary state and functions
  return {
    isLoading: state.isLoading,
    error: state.error,
    errorMessage: getErrorMessage(),
    isConfirmationOpen: state.isConfirmationOpen,
    openConfirmation,
    closeConfirmation,
    executeUnenroll,
  };
}
